import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './WalletManagement.css';

const WalletManagement = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [actionType, setActionType] = useState(''); // 'bonus' | 'penalty' | 'redeem'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/parent/login');
      return;
    }

    loadChildren();
  }, [navigate]);

  useEffect(() => {
    if (selectedChild) {
      loadWalletData(selectedChild.id);
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/families/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChildren(data.children || []);
        if (data.children?.length > 0) {
          setSelectedChild(data.children[0]);
        }
      }
    } catch (error) {
      console.error('자녀 목록 로딩 오류:', error);
    }
  };

  const loadWalletData = async (childId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v0/wallets/${childId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWalletData(data);
      } else {
        console.error('지갑 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('지갑 데이터 로딩 오류:', error);
    }
  };

  const handleTransaction = async (e) => {
    e.preventDefault();

    if (!selectedChild || !actionType || !amount || !reason.trim()) {
      setMessage('모든 필드를 입력해주세요.');
      return;
    }

    if (parseInt(amount) <= 0) {
      setMessage('올바른 금액을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let method = 'POST';

      if (actionType === 'bonus') {
        endpoint = `/api/v0/wallets/${selectedChild.id}/bonus`;
      } else if (actionType === 'penalty') {
        endpoint = `/api/v0/wallets/${selectedChild.id}/penalty`;
      } else if (actionType === 'redeem') {
        endpoint = `/api/v0/wallets/${selectedChild.id}/redeem`;
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseInt(amount),
          reason: reason.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`✅ ${result.message}`);
        setAmount('');
        setReason('');
        setActionType('');
        await loadWalletData(selectedChild.id); // 지갑 데이터 새로고침
      } else {
        const error = await response.json();
        throw new Error(error.error || '처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('거래 처리 오류:', error);
      setMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'earn': return '💰';
      case 'bonus': return '🎁';
      case 'penalty': return '⚠️';
      case 'redeem': return '🛒';
      default: return '📊';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'earn':
      case 'bonus':
        return 'positive';
      case 'penalty':
      case 'redeem':
        return 'negative';
      default:
        return 'neutral';
    }
  };

  if (!selectedChild) {
    return <div className="loading">자녀 정보를 불러오는 중...</div>;
  }

  return (
    <div className="wallet-management">
      <div className="wallet-header">
        <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
          ← 뒤로가기
        </button>
        <h1>도토리 지갑 관리</h1>
      </div>

      {message && (
        <div className={`message ${message.includes('❌') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* 자녀 선택 */}
      <div className="child-selector">
        <label>자녀 선택:</label>
        <div className="child-tabs">
          {children.map((child) => (
            <button
              key={child.id}
              className={`child-tab ${selectedChild.id === child.id ? 'active' : ''}`}
              onClick={() => setSelectedChild(child)}
            >
              {child.name}
            </button>
          ))}
        </div>
      </div>

      {/* 지갑 현황 */}
      {walletData && (
        <div className="wallet-overview">
          <h2>💳 {selectedChild.name}의 지갑</h2>
          <div className="balance-card">
            <div className="current-balance">
              <span className="balance-label">현재 잔액</span>
              <span className="balance-amount">{formatCurrency(walletData.currentBalance)}원</span>
            </div>
            <div className="balance-stats">
              <div className="stat-item">
                <span>총 획득</span>
                <span className="positive">+{formatCurrency(walletData.totalEarned)}원</span>
              </div>
              <div className="stat-item">
                <span>총 사용</span>
                <span className="negative">-{formatCurrency(walletData.totalSpent)}원</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 거래 입력 폼 */}
      <div className="transaction-form">
        <h3>🔄 도토리 관리</h3>

        <form onSubmit={handleTransaction}>
          <div className="action-selector">
            <label>거래 유형:</label>
            <div className="action-buttons">
              <button
                type="button"
                className={`action-btn ${actionType === 'bonus' ? 'active bonus' : ''}`}
                onClick={() => setActionType('bonus')}
              >
                🎁 보너스 지급
              </button>
              <button
                type="button"
                className={`action-btn ${actionType === 'penalty' ? 'active penalty' : ''}`}
                onClick={() => setActionType('penalty')}
              >
                ⚠️ 벌점 차감
              </button>
              <button
                type="button"
                className={`action-btn ${actionType === 'redeem' ? 'active redeem' : ''}`}
                onClick={() => setActionType('redeem')}
              >
                🛒 도토리 사용
              </button>
            </div>
          </div>

          {actionType && (
            <>
              <div className="form-group">
                <label htmlFor="amount">금액 (도토리):</label>
                <input
                  type="number"
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="1"
                  max="50000"
                  placeholder="금액을 입력하세요"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="reason">사유:</label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    actionType === 'bonus' ? '보너스 지급 사유를 입력하세요 (예: 특별히 잘한 일)' :
                    actionType === 'penalty' ? '벌점 사유를 입력하세요 (예: 약속 위반)' :
                    '사용 목적을 입력하세요 (예: 문구류 구입)'
                  }
                  rows="3"
                  maxLength="200"
                  required
                />
                <small>{reason.length}/200</small>
              </div>

              <button
                type="submit"
                className={`btn-submit ${actionType}`}
                disabled={loading}
              >
                {loading ? '처리 중...' :
                 actionType === 'bonus' ? '🎁 보너스 지급하기' :
                 actionType === 'penalty' ? '⚠️ 벌점 차감하기' :
                 '🛒 도토리 사용하기'}
              </button>
            </>
          )}
        </form>
      </div>

      {/* 거래 내역 */}
      {walletData?.recentTransactions && (
        <div className="transaction-history">
          <h3>📋 최근 거래 내역</h3>
          {walletData.recentTransactions.length === 0 ? (
            <div className="no-transactions">아직 거래 내역이 없습니다.</div>
          ) : (
            <div className="transactions-list">
              {walletData.recentTransactions.map((transaction) => (
                <div key={transaction.id} className="transaction-item">
                  <div className="transaction-left">
                    <span className="transaction-icon">
                      {getTransactionIcon(transaction.transaction_type)}
                    </span>
                    <div className="transaction-info">
                      <div className="transaction-desc">{transaction.description}</div>
                      <div className="transaction-date">{formatDate(transaction.created_at)}</div>
                    </div>
                  </div>
                  <div className="transaction-right">
                    <span className={`transaction-amount ${getTransactionColor(transaction.transaction_type)}`}>
                      {transaction.transaction_type === 'earn' || transaction.transaction_type === 'bonus' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}원
                    </span>
                    <div className="balance-after">
                      잔액: {formatCurrency(transaction.balance_after)}원
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WalletManagement;